// ─── Public Status Page — the systems picker ─────────────────────────
//
// ONE picker, used by BOTH the subscribe form and the manage page. It lived
// inside SubscribeSheet until the manage page needed it too, and copying it
// would have been the fourth time in this subsystem that one rule ended up with
// two implementations — the wire contract, the reach count and the audience
// filter all shipped bugs that way. A visitor who ticks "Classic" on signup and
// comes back to change it must see the same tree, indented the same way, with
// the same search.
//
// ── NONE SELECTED MEANS ALL ──
// Empty is not "nothing", it is "no filter" — the rule the fan-out implements
// (services/status/subscribers.recipientsFor). Said out loud in the count line
// below rather than left to be discovered.
//
// Selection state is the CALLER'S: the subscribe form starts empty and the
// manage page starts from what the subscriber already has. Only the view state
// — which branches are open, what is typed in the search — lives here, because
// nothing outside cares about it.

import { useCallback, useMemo, useState } from 'react';
import { countCheckedSystems, systemCheckStates, toggleSystemSelection } from '@pcp/shared';
import { SIcon } from '../ui/SIcon';
import { VisuallyHidden } from '../ui/VisuallyHidden';
import { da } from '../i18n/da';
import type { StatusSubscribeComponent } from '../lib/statusApi';

interface TreeNode extends StatusSubscribeComponent {
  children: TreeNode[];
}

/** Rebuild the tree from the flat (id, parentId) list the API returns. A node
 *  whose parent is missing is promoted to a root rather than dropped — losing a
 *  system from the picker would silently narrow what a visitor can ask for. */
function buildTree(flat: readonly StatusSubscribeComponent[]): TreeNode[] {
  const byId = new Map<string, TreeNode>(flat.map((c) => [c.id, { ...c, children: [] }]));
  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    const parent = node.parentId ? byId.get(node.parentId) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

/** Ids matching the query, PLUS every ancestor of a match — otherwise a matching
 *  grandchild would have nothing to hang off and the tree would render empty. */
function matchingIds(roots: readonly TreeNode[], query: string): Set<string> | null {
  const q = query.trim().toLowerCase();
  if (!q) return null; // null = no filter, show everything
  const keep = new Set<string>();
  const walk = (node: TreeNode, trail: string[]): void => {
    if (node.name.toLowerCase().includes(q)) {
      keep.add(node.id);
      for (const id of trail) keep.add(id);
    }
    for (const child of node.children) walk(child, [...trail, node.id]);
  };
  for (const root of roots) walk(root, []);
  return keep;
}

export interface SystemPickerProps {
  /** The flat option list, straight off /subscribe/session or /manage. */
  components: readonly StatusSubscribeComponent[];
  /** Selected ids. EMPTY = every system. */
  selected: readonly string[];
  /**
   * The WHOLE next selection, not one id.
   *
   * Ticking a parent selects its entire subtree (see toggle below), so a single
   * click can change several ids at once — an `onToggle(id)` caller would have
   * to know the tree to apply that, and the tree lives here.
   */
  onChange: (next: string[]) => void;
  /** Distinct per mount — two pickers on one page must not share an id. */
  labelId: string;
}

export function SystemPicker({ components, selected, onChange, labelId }: SystemPickerProps) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const tree = useMemo(() => buildTree(components), [components]);
  const visibleIds = useMemo(() => matchingIds(tree, query), [tree, query]);

  /**
   * TICKING A PARENT TAKES ITS WHOLE SUBTREE WITH IT; unticking a child removes
   * that child only and leaves the parent PARTIAL; completing a branch ticks the
   * parent again. The rule — and why it must match the fan-out — lives in ONE
   * place, @pcp/shared status-system-selection (b-02007), shared with the admin
   * "Edit subscriber" sheet. It used to be implemented here alone, stored
   * "parent ticked, child unticked", and the fan-out (which reads a stored parent
   * as its whole subtree) kept sending the child anyway.
   */
  const states = useMemo(() => systemCheckStates(components, selected), [components, selected]);
  const toggle = useCallback(
    (node: TreeNode) => onChange(toggleSystemSelection(components, selected, node.id)),
    [components, selected, onChange],
  );

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** One system row plus, when open, its subtree. Recursive: depth is capped at
   *  three server-side. */
  const renderNode = (node: TreeNode): React.ReactNode => {
    if (visibleIds && !visibleIds.has(node.id)) return null;
    const hasChildren = node.children.length > 0;
    // A search forces every surviving branch open — a match the visitor cannot
    // see because its parent is collapsed is the same as no match at all.
    const isOpen = Boolean(visibleIds) || expanded.has(node.id);
    return (
      <div className="sp-tree-node" key={node.id}>
        <div className="sp-tree-row" style={{ paddingLeft: `calc(var(--space-4) * ${node.depth - 1})` }}>
          {hasChildren ? (
            <button
              type="button"
              className="sp-tree-toggle"
              aria-expanded={isOpen}
              onClick={() => toggleExpanded(node.id)}
            >
              <SIcon name="chevronRight" size={14} />
              <VisuallyHidden>
                {isOpen ? da.subscribeCollapseSystem(node.name) : da.subscribeExpandSystem(node.name)}
              </VisuallyHidden>
            </button>
          ) : (
            <span className="sp-tree-toggle sp-tree-toggle--empty" aria-hidden="true" />
          )}
          <label className="sp-check-row sp-tree-label">
            <input
              type="checkbox"
              checked={states.get(node.id) === 'checked'}
              ref={(el) => { if (el) el.indeterminate = states.get(node.id) === 'partial'; }}
              onChange={() => toggle(node)}
            />
            {node.name}
          </label>
        </div>
        {hasChildren && isOpen && <div className="sp-tree-children">{node.children.map(renderNode)}</div>}
      </div>
    );
  };

  if (components.length === 0) return null;

  return (
    // A <fieldset>/<legend> would be the textbook markup, but a fieldset in a
    // flex container still lays its legend out inconsistently across engines. A
    // labelled group carries the same semantics.
    <div className="sp-field" role="group" aria-labelledby={labelId}>
      <span className="sp-label-text" id={labelId}>
        {da.subscribeSystems}
      </span>
      <p className="sp-help">{da.subscribeSystemsHint}</p>
      <input
        className="sp-search-input"
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={da.subscribeSystemsSearch}
        aria-label={da.subscribeSystemsSearch}
      />
      <div className="sp-tree">
        {visibleIds && visibleIds.size === 0 ? (
          <p className="sp-help">{da.subscribeNoSystemMatch}</p>
        ) : (
          tree.map(renderNode)
        )}
      </div>
      <p className="sp-help" aria-live="polite">
        {selected.length === 0 ? da.subscribeSystemsAll : da.subscribeSystemsCount(countCheckedSystems(components, selected))}
      </p>
    </div>
  );
}
